/**
 * CompareStatsTable.jsx — Tableau « Statistiques comparatives » de la comparaison.
 *
 * Une ligne par indicateur de chaque mesure de compareMetrics.js (P95 partagé,
 * D200). Couleur d'alerte selon les seuils pmThresholds.json ; ✓ = meilleure
 * valeur PM (bestValueIndexes) ; mesure absente = « Non mesuré », jamais un
 * tiret ni un zéro. Valeurs calculées par compareTableRows, partagé avec le PDF.
 *
 * Plage d'analyse (10.5.297) : `loaded` porte les relevés déjà limités à la
 * plage ; `quality` donne par session la durée et le nombre de mesures
 * retenus (en-têtes) ; `hideBestFields` les mesures sans coche (trop peu de
 * mesures dans la plage).
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { semanticColors } from '../constants/semanticColors';
import { formatNumberLocale } from '../utils/formatters';
import { getComparePalette } from '../constants/comparePalette';
import { compareTableRows, levelTextColor, metricDecimals } from './compareMetrics';
import { withoutBestMarks } from './analysisRange';
import { columnHeader } from './analysisRangeText';

const CompareStatsTable = ({ loaded, quality, hideBestFields = [], colors: c, darkMode }) => {
  const { t: tr } = useTranslation();
  const palette = getComparePalette(darkMode);

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
      <thead>
        <tr>
          <th style={{ padding: '8px 12px', textAlign: 'left', color: c.textSec,
            fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em',
            borderBottom: `2px solid ${c.border}`, fontWeight: '600' }}>
            {tr('sessions.compare.parameter')}
          </th>
          {loaded.map((s, i) => (
            <th key={i} style={{
              padding: '8px 12px', textAlign: 'center', borderBottom: `2px solid ${c.border}`,
              fontSize: '11px', fontWeight: '600', color: palette.styleAt(i).color,
              maxWidth: '140px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%',
                  background: palette.styleAt(i).color, flexShrink: 0 }} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {s.session?.title || tr('sessions.compare.fallbackName', { n: i + 1 })}
                </span>
              </div>
              <div style={{ fontSize: '10px', color: c.textSec, fontWeight: '400',
                marginTop: '2px', textTransform: 'none', letterSpacing: 0 }}>
                {quality[i] && columnHeader(quality[i], tr)}
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {withoutBestMarks(compareTableRows(loaded), hideBestFields).map((row, ri) => (
            <tr key={row.labelKey} style={{
              background: ri % 2 === 0
                ? (darkMode ? 'rgba(255,255,255,0.02)' : '#FAFAFA') : 'transparent'
            }}>
              <td style={{ padding: '9px 12px', color: c.textSec, fontSize: '12px',
                fontWeight: '500', borderBottom: `1px solid ${c.border}` }}>
                {tr(row.labelKey)}
              </td>
              {row.cells.map(({ value: val, level, isBest }, i) => {
                const alertColor = levelTextColor(level);
                return (
                  <td key={i} style={{
                    padding: '9px 12px', textAlign: 'center',
                    borderBottom: `1px solid ${c.border}`,
                    fontWeight: isBest ? '700' : '400',
                    color: val == null ? c.textSec
                      : (alertColor || (isBest ? semanticColors.greenText : c.text)),
                    fontSize: '13px',
                  }}>
                    {val == null ? tr('common.notMeasured') : formatNumberLocale(val, metricDecimals(row.metric))}
                    {val != null && <span style={{ fontSize: '10px', color: c.textSec }}> {row.metric.tableUnit}</span>}
                    {isBest && <span style={{ fontSize: '10px', marginLeft: '4px' }}>✓</span>}
                  </td>
                );
              })}
            </tr>
        ))}
      </tbody>
    </table>
  );
};

export default CompareStatsTable;
